// src/services/documentService.js
// Handles document numbering (correlative) and team company settings for PDF generation.

import { getSupabaseClient } from "./supabaseClient.js";

// ─── Team Settings ────────────────────────────────────────────────────────────

/**
 * Fetches the company/legal data for a team.
 * Returns an object with: company_name, cif, fiscal_address, fiscal_city,
 * fiscal_zip, phone, email, web, iban
 */
export async function getTeamSettings(teamId) {
    if (!teamId) return {};
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("team_settings")
        .select("*")
        .eq("team_id", teamId)
        .maybeSingle();

    if (error) {
        console.error("[documentService] getTeamSettings error:", error);
        return {};
    }
    return data || {};
}

/**
 * Upserts team settings (company legal data).
 */
export async function updateTeamSettings(teamId, settings) {
    if (!teamId) throw new Error("teamId requerido");
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("team_settings")
        .upsert({ team_id: teamId, ...settings }, { onConflict: "team_id" })
        .select()
        .single();

    if (error) throw error;
    return data;
}

// ─── Document Registry (correlative numbering) ────────────────────────────────

/**
 * Previews the next correlative document number for a given type WITHOUT incrementing it.
 */
export async function previewNextDocNumber(teamId, docType) {
    if (!teamId || !docType) return `${docType}-${new Date().getFullYear()}-XXXX`;

    const supabase = getSupabaseClient();
    const year = new Date().getFullYear();

    const { data, error } = await supabase
        .from("document_registry")
        .select("last_seq")
        .eq("team_id", teamId)
        .eq("doc_type", docType)
        .eq("year", year)
        .maybeSingle();

    if (error) {
        console.warn("[documentService] previewNextDocNumber error:", error);
    }

    const nextSeq = (data?.last_seq || 0) + 1;
    return `${docType}-${year}-${String(nextSeq).padStart(4, "0")}`;
}


/**
 * Gets the next correlative document number for a given type.
 * Uses an atomic UPDATE to prevent race conditions.
 *
 * docType: 'F' → Factura, 'PRO' → Proforma, 'C' → Contrato, 'CONF' → Confirmación, 'P' → Presupuesto
 * Returns a string like "F-2026-0003"
 */
export async function incrementDocNumber(teamId, docType) {
    if (!teamId || !docType) {
        // Fallback: local random (shouldnt happen in production)
        const year = new Date().getFullYear();
        const rand = String(Math.floor(Math.random() * 9000) + 1000);
        return `${docType}-${year}-${rand}`;
    }

    const supabase = getSupabaseClient();
    const year = new Date().getFullYear();

    // Try to atomically increment
    // First, ensure the row exists
    await supabase
        .from("document_registry")
        .upsert(
            { team_id: teamId, doc_type: docType, year, last_seq: 0 },
            { onConflict: "team_id,doc_type,year", ignoreDuplicates: true }
        );

    // Atomic increment using RPC
    const { data: newSeq, error } = await supabase.rpc("increment_doc_number", {
        p_team_id: teamId,
        p_doc_type: docType,
        p_year: year,
    });

    if (error || !newSeq) {
        // Fallback to reading and updating manually (last resort)
        const { data: row } = await supabase
            .from("document_registry")
            .select("last_seq")
            .eq("team_id", teamId)
            .eq("doc_type", docType)
            .eq("year", year)
            .single();

        const seq = (row?.last_seq || 0) + 1;
        await supabase
            .from("document_registry")
            .update({ last_seq: seq })
            .eq("team_id", teamId)
            .eq("doc_type", docType)
            .eq("year", year);

        return `${docType}-${year}-${String(seq).padStart(4, "0")}`;
    }

    return `${docType}-${year}-${String(newSeq).padStart(4, "0")}`;
}


// ─── Company data builder ─────────────────────────────────────────────────────

/**
 * Builds the company data object for PDF generation.
 * Falls back to placeholder values if team_settings is not configured.
 */
export function buildCompanyData(settings = {}) {
    return {
        name:          settings.company_name || "LOS TOLDOS ENTERTAINMENT S.L.",
        cif:           settings.cif          || "B-PENDIENTE",
        address:       settings.fiscal_address || "Getxo, Bizkaia",
        city:          settings.fiscal_city   || "",
        zip:           settings.fiscal_zip    || "",
        // El contrato cita el domicilio social completo ("..., Loiu, Bizkaia").
        province:      settings.fiscal_province || "",
        phone:         settings.phone         || "+34 XXX XXX XXX",
        email:         settings.email         || "info@wearelostoldos.com",
        web:           settings.web           || "www.wearelostoldos.com",
        iban:          settings.iban          || "",
        isConfigured:  !!(settings.cif && settings.iban),
    };
}

// ─── Validation helpers ───────────────────────────────────────────────────────

/**
 * Returns a list of missing fields for a given document type.
 * docType: 'factura' | 'contrato' | 'proforma' | 'presupuesto' | 'confirmacion'
 */
export function validateDocumentData({ company, contact, docType }) {
    const errors = [];

    // Company checks — critical for all official docs
    if (!company?.cif || company.cif === "B-PENDIENTE") {
        errors.push({ field: "company_cif", label: "CIF de la empresa", scope: "company" });
    }
    if (!company?.iban) {
        errors.push({ field: "company_iban", label: "IBAN de la empresa", scope: "company" });
    }

    // Contact NIF — required for Contrato and Factura
    const needsNif = ["factura", "contrato"].includes(docType);
    if (needsNif && !contact?.nif) {
        errors.push({ field: "contact_nif", label: "NIF/CIF del cliente", scope: "contact" });
    }

    return errors;
}
